import React, { useEffect, useRef, useState } from "react";
import {Bot, Check, ChevronDown, Search, Zap, LockKeyhole} from "lucide-react";

function BotSelector({bots = [],selectedBot = null,loading = false,onBotChange}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick,
      );
    };
  }, []);


  const botRecords = Array.isArray(bots) ? bots : [];
  
  const filteredBots = botRecords.filter((bot) => {
    const name = bot?.name || "";
    const description = bot?.description || "";
    const value = `${name} ${description}`.toLowerCase();

    return value.includes(search.toLowerCase());
  });

  const activeBot = selectedBot || null;
  const botName = activeBot?.name || "Select Bot";

  const getBotIcon = (bot) => {
    if (bot?.icon) return bot.icon;
    return "🤖";
  };

  const handleBotChange = async (bot) => {
    if (loading) return;

    try {
      await onBotChange?.(bot);
    } finally {
      setIsOpen(false);
      setSearch("");
    }
  };

 return (
  <div ref={dropdownRef} className="relative">
    <button
      type="button"
      disabled={loading}
      onClick={() => setIsOpen((prev) => !prev)}
      className={`flex w-full items-center gap-3 rounded-xl border bg-card-bg px-3 py-2.5 text-left transition-all dark:bg-dark-card ${
        isOpen
          ? "border-theme-200 bg-app-bg shadow-[0_0_0_3px_rgba(61,155,210,0.08)] dark:border-dark-primary/40 dark:bg-dark-soft-blue"
          : "border-border-light hover:border-theme-100 hover:bg-app-bg dark:border-dark-border dark:hover:bg-dark-soft-blue"
      } ${
        loading
          ? "cursor-not-allowed opacity-60"
          : ""
      }`}
    >
      {/* Bot Icon */}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-theme-100 bg-theme-50 text-primary dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary">
        {getBotIcon(activeBot)}
      </div>

      {/* Bot Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-[9px] font-medium uppercase tracking-[0.06em] text-text-muted dark:text-dark-text-muted">
            Active Bot
          </p>

          {activeBot?.is_active && (
            <span className="flex items-center gap-1 text-[8px] font-semibold text-primary dark:text-dark-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-[#38bdf8]" />
              Active
            </span>
          )}
        </div>

        <p className="mt-0.5 truncate text-[11px] font-semibold text-text-primary dark:text-dark-text-primary">
          {botName}
        </p>
      </div>

      {/* Chevron */}
      <ChevronDown
        size={15}
        strokeWidth={1.8}
        className={`shrink-0 text-text-muted transition-transform dark:text-dark-text-muted ${
          isOpen
            ? "rotate-180 text-primary dark:text-dark-primary"
            : ""
        }`}
      />
    </button>

    {isOpen && (
      <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-[70] overflow-hidden rounded-xl border border-border-light bg-card-bg shadow-[0_16px_40px_rgba(15,23,42,0.10)] dark:border-dark-border dark:bg-dark-card">

        {/* Search */}
        <div className="border-b border-border-light bg-card-bg p-2 dark:border-dark-border dark:bg-dark-card">
          <div className="flex items-center gap-2 rounded-lg border border-transparent bg-app-bg px-2.5 transition-all focus-within:border-theme-100 focus-within:bg-card-bg focus-within:shadow-[0_0_0_3px_rgba(61,155,210,0.05)] dark:bg-dark-soft-blue dark:focus-within:border-dark-primary/30 dark:focus-within:bg-dark-card-bg">
            <Search
              size={13}
              strokeWidth={1.8}
              className="shrink-0 text-text-muted dark:text-dark-text-muted"
            />

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search bots..."
              className="h-8 w-full bg-transparent text-[10px] text-text-primary outline-none placeholder:text-text-muted dark:text-dark-text-primary dark:placeholder:text-dark-text-muted"
              autoFocus
            />
          </div>
        </div>

        {/* Bots */}
        <div className="max-h-[230px] overflow-y-auto p-1.5">
          {filteredBots.map((bot) => {
            const id = bot?.id;
            const name = bot?.name || "Unnamed Bot";
            const description = bot?.description || "Automation bot";
            const isSelected = activeBot?.id === id;
            const isActive = bot?.is_active ?? false;
            const selectable = isActive;

            return (
              <button
                key={id}
                type="button"
                disabled={ loading || !selectable}
                onClick={() =>
                  handleBotChange(bot)
                }
                className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-all ${
                  !selectable
                    ? "cursor-not-allowed opacity-45"
                    : isSelected
                      ? "bg-theme-50 dark:bg-dark-soft-blue"
                      : "hover:bg-app-bg dark:hover:bg-dark-soft-blue"
                }`}
              >
                {/* Bot Icon */}
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm ${
                    !selectable
                      ? "bg-app-bg grayscale dark:bg-dark-soft-blue"
                      : isSelected
                        ? "border border-theme-100 bg-theme-50 text-primary dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary"
                        : "bg-app-bg dark:bg-dark-soft-blue"
                  }`}
                >
                  {getBotIcon(bot)}
                </div>

                {/* Bot Details */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p
                      className={`truncate text-[10px] font-semibold ${
                        !selectable
                          ? "text-text-muted dark:text-dark-text-muted"
                          : isSelected
                            ? "text-primary dark:text-dark-primary"
                            : "text-text-primary dark:text-dark-text-primary"
                      }`}
                    >
                      {name}
                    </p>

                    {isActive &&
                      selectable && (
                        <span className="flex shrink-0 items-center gap-1 text-[8px] font-semibold text-primary dark:text-dark-primary">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#38bdf8]" />
                          Active
                        </span>
                      )}
                  </div>

                  <p className="mt-0.5 truncate text-[8px] text-text-muted dark:text-dark-text-muted">
                    {description}
                  </p>

                  {/* No Stages */}
                  {!selectable && (
                    <div className="mt-1 flex items-center gap-1">
                      <LockKeyhole
                        size={9}
                        strokeWidth={2}
                        className="text-text-muted dark:text-dark-text-muted"
                      />

                      <span className="text-[7px] font-medium text-text-muted dark:text-dark-text-muted">
                        Bot is inactive
                      </span>
                    </div>
                  )}
                </div>

                {/* Selected */}
                {isSelected &&
                  selectable && (
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-[0_2px_6px_rgba(61,155,210,0.20)] dark:bg-dark-primary">
                      <Check
                        size={11}
                        strokeWidth={2.5}
                      />
                    </div>
                  )}

                {/* Locked */}
                {!selectable && (
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border-light bg-app-bg text-text-muted dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-text-muted">
                    <LockKeyhole
                      size={9}
                      strokeWidth={2}
                    />
                  </div>
                )}
              </button>
            );
          })}

          {/* Empty State */}
          {!filteredBots.length && (
            <div className="px-3 py-7 text-center">
              <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl border border-theme-100 bg-theme-50 text-primary dark:border-dark-border dark:bg-dark-soft-blue dark:text-dark-primary">
                <Bot
                  size={16}
                  strokeWidth={1.8}
                />
              </div>

              <p className="mt-2 text-[10px] font-medium text-text-secondary dark:text-dark-text-secondary">
                No bots found
              </p>

              <p className="mt-0.5 text-[9px] text-text-muted dark:text-dark-text-muted">
                Try another search
              </p>
            </div>
          )}
        </div>

        {/* Footer Info */}
        {filteredBots.length > 0 && (
          <div className="border-t border-border-light bg-app-bg px-3 py-2 dark:border-dark-border dark:bg-dark-soft-blue">
            <div className="flex items-center gap-1.5">
              <Zap
                size={10}
                className="text-primary dark:text-dark-primary"
                strokeWidth={2}
              />

              <span className="text-[8px] text-text-muted dark:text-dark-text-muted">
                The selected bot handles this conversation
              </span>
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);
}

export default BotSelector;